import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, AlertCircle, ShieldAlert, Download, Search, ScrollText, X, Info, ChevronDown } from 'lucide-react';
import { fmtMoney as fmt } from '../../utils/money';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function isoToday() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Mexico_City' }).format(new Date());
}
function isoMonthStart() {
  return `${isoToday().slice(0, 8)}01`;
}

const SEMAFORO = {
  rojo: { dot: 'bg-red-500', badge: 'bg-red-50 text-red-700 border-red-200', label: 'Revisar con la vendedora' },
  amarillo: { dot: 'bg-amber-400', badge: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Revisar' },
  verde: { dot: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Sin alertas relevantes' },
};

// Diccionario: qué es el puntaje por día, cómo se calcula y qué significa cada alerta.
function GuiaPuntaje({ data, abierta, onToggle }) {
  const U = data.umbrales || {};
  const tipos = Object.entries(data.tipos || {}).sort((a, b) => b[1].peso - a[1].peso);
  const ej = (data.resumen || []).find(r => r.dias > 0 && r.puntaje > 0);
  return (
    <div className="bg-white rounded-xl border border-zinc-200" data-testid="guia-puntaje">
      <button onClick={onToggle} className="w-full px-4 py-3 flex items-center gap-2 text-left">
        <Info className="w-4 h-4 text-zinc-500" />
        <span className="font-bold text-zinc-900 flex-1">Cómo leer el puntaje por día</span>
        <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${abierta ? 'rotate-180' : ''}`} />
      </button>
      {abierta && (
        <div className="px-4 pb-4 space-y-4 text-sm text-zinc-700">
          <div className="space-y-1">
            <p>
              <strong>Puntaje</strong>: cada tipo de alerta tiene un <strong>peso</strong> según qué tan grave es.
              Se suma <em>peso × número de días distintos</em> en que apareció esa alerta en el punto.
            </p>
            <p>
              <strong>Puntaje por día</strong> = puntaje ÷ días operados (cortes) en el rango. Así se comparan
              puntos que abrieron distinto número de días: es "cuántos puntos de alerta acumula, en promedio,
              cada día que opera".
            </p>
            {ej && (
              <p className="text-zinc-500">
                Ejemplo: <strong>{ej.punto}</strong> acumuló {ej.puntaje} puntos en {ej.dias} días →{' '}
                {ej.puntaje} ÷ {ej.dias} = <strong>{ej.indice.toFixed(2)}</strong>.
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
              <p className="font-bold text-emerald-700">Menos de {U.semaforo_amarillo}</p>
              <p className="text-xs text-emerald-700">Verde · sin alertas relevantes. Operación normal.</p>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="font-bold text-amber-700">{U.semaforo_amarillo} a menos de {U.semaforo_rojo}</p>
              <p className="text-xs text-amber-700">Amarillo · revisar. Hay alertas casi a diario; ver de qué tipo son.</p>
            </div>
            <div className="rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="font-bold text-red-700">{U.semaforo_rojo} o más</p>
              <p className="text-xs text-red-700">Rojo · revisar con la vendedora. Varias alertas graves casi todos los días.</p>
            </div>
          </div>
          <p className="text-xs text-zinc-500">
            Como referencia: {U.semaforo_rojo} equivale, por ejemplo, a un sobrante de caja (peso 2) todos los días, o a
            un corte exacto (1) más una entrada sin confirmar (2) en dos de cada tres días. Un punto sin cortes en el
            rango no tiene puntaje por día y, si tiene alertas, se marca amarillo.
          </p>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-zinc-500 bg-zinc-50">
                <tr>
                  <th className="text-left px-2 py-2">Alerta</th>
                  <th className="text-center px-2 py-2">Peso</th>
                  <th className="text-left px-2 py-2">Qué significa</th>
                </tr>
              </thead>
              <tbody>
                {tipos.map(([k, t]) => (
                  <tr key={k} className="border-t border-zinc-100 align-top">
                    <td className="px-2 py-2 font-medium text-zinc-900 whitespace-nowrap">{t.titulo}</td>
                    <td className="px-2 py-2 text-center">
                      <span className={`inline-block min-w-[24px] px-1.5 py-0.5 rounded text-xs font-bold ${t.peso >= 3 ? 'bg-red-50 text-red-700' : t.peso > 0 ? 'bg-amber-50 text-amber-700' : 'bg-zinc-100 text-zinc-500'}`}>
                        {t.peso}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-zinc-600">{t.descripcion}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-zinc-500">
            Peso 0 = alerta informativa: se lista para contexto pero no suma puntaje. El puntaje orienta dónde mirar
            primero; la decisión se toma revisando el detalle y la bitácora de cada día.
          </p>
        </div>
      )}
    </div>
  );
}

export function AdminAlertas() {
  const navigate = useNavigate();
  const [desde, setDesde] = useState(isoMonthStart);
  const [hasta, setHasta] = useState(isoToday);
  const [puntos, setPuntos] = useState([]);
  const [puntoFiltro, setPuntoFiltro] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [seleccion, setSeleccion] = useState(null); // punto_venta_id elegido en el resumen
  const [tipoFiltro, setTipoFiltro] = useState('');
  const [verInformativas, setVerInformativas] = useState(false);
  const [guiaAbierta, setGuiaAbierta] = useState(false);

  useEffect(() => {
    axios.get(`${API}/puntos-venta`)
      .then(({ data: pv }) => setPuntos(pv.sort((a, b) => a.name.localeCompare(b.name, 'es'))))
      .catch(() => {});
  }, []);

  const params = () => ({ desde, hasta, ...(puntoFiltro ? { punto_venta_id: puntoFiltro } : {}) });

  const generar = async () => {
    setLoading(true);
    setError('');
    setSeleccion(null);
    setTipoFiltro('');
    try {
      const { data: res } = await axios.get(`${API}/admin/reporte-alertas`, { params: params() });
      setData(res);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al generar el reporte');
    } finally {
      setLoading(false);
    }
  };

  const descargar = async () => {
    setDownloading(true);
    setError('');
    try {
      const qs = new URLSearchParams(params()).toString();
      const response = await fetch(`${API}/admin/reporte-alertas/excel?${qs}`, { credentials: 'include' });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Error al generar el Excel');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `alertas_${desde}_${hasta}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Error al descargar');
    } finally {
      setDownloading(false);
    }
  };

  const tipos = data?.tipos || {};
  const resumen = data?.resumen || [];
  const conteo = useMemo(() => ({
    rojo: resumen.filter(r => r.semaforo === 'rojo').length,
    amarillo: resumen.filter(r => r.semaforo === 'amarillo').length,
    verde: resumen.filter(r => r.semaforo === 'verde').length,
  }), [resumen]);

  const alertas = useMemo(() => (data?.alertas || []).filter(a =>
    (!seleccion || a.punto_venta_id === seleccion)
    && (!tipoFiltro || a.tipo === tipoFiltro)
    && (verInformativas || a.peso > 0 || a.tipo === tipoFiltro)
  ), [data, seleccion, tipoFiltro, verInformativas]);

  const tiposPresentes = useMemo(() => {
    const s = {};
    (data?.alertas || []).forEach(a => {
      if (!seleccion || a.punto_venta_id === seleccion) s[a.tipo] = (s[a.tipo] || 0) + 1;
    });
    return Object.entries(s).sort((a, b) => (tipos[b[0]]?.peso || 0) - (tipos[a[0]]?.peso || 0));
  }, [data, seleccion, tipos]);

  const puntoSel = resumen.find(r => r.punto_venta_id === seleccion);

  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="bg-white px-4 py-3 border-b border-zinc-200">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center border border-zinc-300 rounded-lg"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-zinc-900">Alertas de cuadre</h1>
            <p className="text-xs text-zinc-500">Inconsistencias de caja, producto, abonos y tránsitos por punto</p>
          </div>
        </div>
      </div>

      <div className="px-4 py-4 space-y-4 pb-24 max-w-6xl mx-auto">
        {/* Filtros */}
        <div className="bg-white rounded-xl border border-zinc-200 p-4 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Desde</label>
              <input type="date" value={desde} max={hasta} onChange={e => setDesde(e.target.value)}
                className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm font-medium" />
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Hasta</label>
              <input type="date" value={hasta} min={desde} max={isoToday()} onChange={e => setHasta(e.target.value)}
                className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm font-medium" />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-zinc-500 mb-1 block">Punto de venta</label>
              <select value={puntoFiltro} onChange={e => setPuntoFiltro(e.target.value)}
                className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm font-medium bg-white">
                <option value="">Todos los puntos</option>
                {puntos.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={generar} disabled={loading}
              className="flex-1 h-11 bg-zinc-900 text-white rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
              {loading ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                : <Search className="w-4 h-4" />}
              Generar
            </button>
            <button onClick={descargar} disabled={downloading}
              className="h-11 px-4 border border-zinc-300 rounded-lg font-medium flex items-center gap-2 disabled:opacity-50">
              {downloading ? <span className="w-4 h-4 border-2 border-zinc-300 border-t-zinc-700 rounded-full animate-spin" />
                : <Download className="w-4 h-4" />}
              Excel
            </button>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" /> {error}
          </div>
        )}

        {!data && !loading && (
          <div className="text-center py-16 text-zinc-400">
            <ShieldAlert className="w-10 h-10 mx-auto mb-2" />
            <p className="text-sm">Elige el rango de fechas y presiona Generar</p>
          </div>
        )}

        {data && (
          <>
            {/* Semáforo global */}
            <div className="grid grid-cols-3 gap-3">
              {['rojo', 'amarillo', 'verde'].map(s => (
                <div key={s} className={`rounded-xl border p-3 ${SEMAFORO[s].badge}`}>
                  <p className="text-2xl font-bold">{conteo[s]}</p>
                  <p className="text-xs font-medium">{SEMAFORO[s].label}</p>
                </div>
              ))}
            </div>

            <GuiaPuntaje data={data} abierta={guiaAbierta} onToggle={() => setGuiaAbierta(v => !v)} />

            {/* Resumen por punto */}
            <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-zinc-100">
                <h2 className="font-bold text-zinc-900">Resumen por punto</h2>
                <p className="text-xs text-zinc-500">Ordenado por riesgo. Toca un punto para ver sus alertas.</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-xs text-zinc-500">
                    <tr>
                      <th className="text-left px-3 py-2">Punto</th>
                      <th className="text-right px-3 py-2">Días</th>
                      <th className="text-right px-3 py-2">Ventas</th>
                      <th className="text-right px-3 py-2">Dif. caja neta</th>
                      <th className="text-right px-3 py-2">Faltante prod.</th>
                      <th className="text-right px-3 py-2">Cortes exactos</th>
                      <th className="text-right px-3 py-2">Alertas</th>
                      <th className="text-right px-3 py-2">
                        <button onClick={() => setGuiaAbierta(true)} className="inline-flex items-center gap-1 hover:text-zinc-800" title="¿Qué significa?">
                          Puntaje/día <Info className="w-3.5 h-3.5" />
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {resumen.map(r => (
                      <tr key={r.punto_venta_id}
                        onClick={() => { setSeleccion(seleccion === r.punto_venta_id ? null : r.punto_venta_id); setTipoFiltro(''); }}
                        className={`border-t border-zinc-100 cursor-pointer hover:bg-zinc-50 ${seleccion === r.punto_venta_id ? 'bg-zinc-100' : ''}`}>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${SEMAFORO[r.semaforo].dot}`} />
                            <div className="min-w-0">
                              <p className="font-medium text-zinc-900 truncate">{r.punto}</p>
                              {r.vendedoras.length > 0 && (
                                <p className="text-xs text-zinc-400 truncate max-w-[220px]">{r.vendedoras.join(', ')}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="text-right px-3 py-2 font-mono">{r.dias}</td>
                        <td className="text-right px-3 py-2 font-mono">{fmt(r.ventas, 0)}</td>
                        <td className={`text-right px-3 py-2 font-mono ${r.dif_neta > 0 ? 'text-emerald-700' : r.dif_neta < 0 ? 'text-red-600' : ''}`}>
                          {fmt(r.dif_neta, 0)}
                        </td>
                        <td className="text-right px-3 py-2 font-mono">{fmt(r.faltante_producto, 0)}</td>
                        <td className="text-right px-3 py-2 font-mono">{r.cortes_exactos}/{r.cortes_vendedora}</td>
                        <td className="text-right px-3 py-2 font-mono">{r.total_alertas}</td>
                        <td className="text-right px-3 py-2 font-mono font-bold">{r.indice.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Detalle */}
            <div className="bg-white rounded-xl border border-zinc-200">
              <div className="px-4 py-3 border-b border-zinc-100 space-y-2">
                <div className="flex items-center gap-2">
                  <h2 className="font-bold text-zinc-900 flex-1">
                    Alertas {puntoSel ? `· ${puntoSel.punto}` : 'de todos los puntos'}
                    <span className="ml-2 text-xs font-normal text-zinc-400">{alertas.length}</span>
                  </h2>
                  {seleccion && (
                    <button onClick={() => setSeleccion(null)} className="text-xs text-zinc-500 flex items-center gap-1">
                      <X className="w-3 h-3" /> Quitar punto
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <button onClick={() => setTipoFiltro('')}
                    className={`px-2.5 py-1 rounded-full text-xs border ${!tipoFiltro ? 'bg-zinc-900 text-white border-zinc-900' : 'border-zinc-300 text-zinc-600'}`}>
                    Todas
                  </button>
                  {tiposPresentes.map(([t, n]) => (
                    <button key={t} onClick={() => setTipoFiltro(tipoFiltro === t ? '' : t)}
                      className={`px-2.5 py-1 rounded-full text-xs border ${tipoFiltro === t ? 'bg-zinc-900 text-white border-zinc-900' : 'border-zinc-300 text-zinc-600'} ${tipos[t]?.peso === 0 ? 'italic' : ''}`}>
                      {tipos[t]?.titulo || t} · {n}
                    </button>
                  ))}
                </div>
                <label className="flex items-center gap-2 text-xs text-zinc-500">
                  <input type="checkbox" checked={verInformativas} onChange={e => setVerInformativas(e.target.checked)} />
                  Mostrar alertas informativas (no suman puntaje)
                </label>
              </div>

              {alertas.length === 0 ? (
                <p className="text-center text-sm text-zinc-400 py-10">Sin alertas con estos filtros</p>
              ) : (
                <ul className="divide-y divide-zinc-100">
                  {alertas.slice(0, 500).map((a, i) => (
                    <li key={i} className="px-4 py-3">
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                            <span className="text-xs font-mono text-zinc-500">{a.fecha}</span>
                            {!seleccion && <span className="text-xs font-semibold text-zinc-700">{a.punto}</span>}
                            <span className={`text-xs px-1.5 py-0.5 rounded ${a.peso >= 3 ? 'bg-red-50 text-red-700' : a.peso > 0 ? 'bg-amber-50 text-amber-700' : 'bg-zinc-100 text-zinc-500'}`}>
                              {a.titulo}
                            </span>
                            {a.monto != null && <span className="text-xs font-mono font-bold text-zinc-800">{fmt(a.monto)}</span>}
                          </div>
                          <p className="text-sm text-zinc-800 mt-1">{a.detalle}</p>
                          {a.evidencia && <p className="text-xs text-zinc-500 mt-0.5 break-words">{a.evidencia}</p>}
                          {a.vendedora && <p className="text-xs text-zinc-400 mt-0.5">{a.vendedora}</p>}
                        </div>
                        {a.fecha && (
                          <button
                            onClick={() => navigate(`/admin/bitacora?punto=${a.punto_venta_id}&fecha=${a.fecha}`)}
                            title="Ver en bitácora"
                            className="shrink-0 w-9 h-9 flex items-center justify-center border border-zinc-200 rounded-lg text-zinc-500 hover:bg-zinc-50">
                            <ScrollText className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                  {alertas.length > 500 && (
                    <li className="px-4 py-3 text-xs text-zinc-500 text-center">
                      Se muestran 500 de {alertas.length}. Filtra por punto o tipo, o descarga el Excel para verlas todas.
                    </li>
                  )}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
